/* eslint-disable react/no-unstable-nested-components */
import { FC, useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import LockOutlined from '@mui/icons-material/LockOutlined';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Header from '@/components/Header/Header';
import { useSTKContext } from '@/context/context';
import { useFranchiseeBundleAddOns } from '@/services/namedPrice/namedPrice.service';
import { FranchiseeBundleAddOnRow, FranchiseeBundleAddOnStoreGroupPrice } from '@/types/namedPrice.types';
import ModalStores from '@/pages/Franchisee/Menus/StoreMenuDetails/internal/ModalStores.module';
import BundleUpchargesTable from '@/pages/Franchisee/Bundles/BundleUpcharges/components/BundleUpchargeTable';
import EditBundleAddOnDrawer from '@/pages/Franchisee/Bundles/BundleAddOns/EditBundleAddOnDrawer/EditBundleAddOnDrawer';
import { NamedPricePriceDeviationsDrawer } from '@/pages/Franchisee/Bundles/BundleAddOns/NamedPricePriceDeviationsDrawer/NamedPricePriceDeviationsDrawer';
import { ColumnDef, Row } from '@tanstack/react-table';
import styles from './FZBundleAddOnsView.module.css';
import priceInfoStyles from '@/pages/Franchisee/Bundles/BundleUpcharges/components/PrinceInfo.module.css';

const FZBundleAddOnsView: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { storesAndGroups } = useSTKContext();
  const [modalOpen, setModalOpen] = useState(false);
  const [editAddOnModalOpen, setEditAddOnModalOpen] = useState(false);
  const [selectedAddOn, setSelectedAddOn] = useState<FranchiseeBundleAddOnRow | null>(null);
  const [priceDeviationsDrawer, setPriceDeviationsDrawer] = useState<{
    isOpen: boolean;
    namedPriceId: string;
    selectedGroupName: string;
  }>({ isOpen: false, namedPriceId: '', selectedGroupName: '' });

  const selectedStoreNumbers = useMemo(() => {
    if (!storesAndGroups) return [];
    if (storesAndGroups.origin === 'tab-store' && storesAndGroups.stores) {
      return storesAndGroups.stores.map((s) => s.storeNo);
    }
    if (storesAndGroups.origin === 'tab-stores-groups' && storesAndGroups.storeGroups) {
      return storesAndGroups.storeGroups.flatMap((g) => g.stores);
    }
    return [];
  }, [storesAndGroups]);

  const selectedCount = selectedStoreNumbers.length;

  const { data, isLoading, isError, refetch } = useFranchiseeBundleAddOns(true, storesAndGroups);

  useEffect(() => {
    const hasStores = (storesAndGroups?.stores?.length ?? 0) > 0 || (storesAndGroups?.storeGroups?.length ?? 0) > 0;
    if (!hasStores) {
      navigate('/bundles/add-ons', { replace: true });
    }
  }, [navigate, storesAndGroups]);

  const addOnsSortedAlphabetically = useMemo(() => {
    const addOns = data?.addOns ?? [];
    return [...addOns].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' }));
  }, [data?.addOns]);

  const storeGroupsForColumns = useMemo(() => {
    const byName = (a: { storeGroup: string }, b: { storeGroup: string }) =>
      (a.storeGroup ?? '').localeCompare(b.storeGroup ?? '', undefined, { sensitivity: 'base' });
    if (storesAndGroups?.storeGroups?.length) {
      return [...storesAndGroups.storeGroups]
        .map((g) => ({
          storeGroup: g.storeGroup,
          selected: g.stores?.length ?? 0,
          total: g.storeCount ?? 0,
        }))
        .sort(byName);
    }
    if (
      storesAndGroups?.origin === 'tab-store' &&
      storesAndGroups?.stores?.length &&
      storesAndGroups?.countByStoreGroup?.length
    ) {
      const byGroup = storesAndGroups.stores.reduce((acc, s) => {
        const g = s.storeGroup ?? '';
        if (!acc[g]) acc[g] = [];
        acc[g].push(s.storeNo);
        return acc;
      }, {} as Record<string, string[]>);
      return storesAndGroups.countByStoreGroup
        .filter(({ storeGroup }) => (byGroup[storeGroup]?.length ?? 0) > 0)
        .map(({ storeGroup, count }) => ({
          storeGroup,
          selected: byGroup[storeGroup]?.length ?? 0,
          total: count,
        }))
        .sort(byName);
    }
    return [];
  }, [storesAndGroups]);

  const parsePriceForSort = (value: unknown): number => {
    const s = typeof value === 'string' ? value : '';
    const cleaned = s.replace(/^\$/, '').trim();
    if (!cleaned) return Number.POSITIVE_INFINITY;
    const firstPart = cleaned.includes(' - ') ? cleaned.split(' - ')[0]?.trim() ?? cleaned : cleaned;
    const num = parseFloat(firstPart.replace(/[^0-9.]/g, ''));
    return Number.isNaN(num) ? Number.POSITIVE_INFINITY : num;
  };

  const columns = useMemo(() => {
    const list: ColumnDef<FranchiseeBundleAddOnRow, unknown>[] = [
      {
        id: 'adminLock',
        accessorKey: 'isLock',
        header: '',
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }: { row: Row<FranchiseeBundleAddOnRow> }) =>
          row.original.isLock ? (
            <div className={styles.lockIconCell}>
              <LockOutlined
                sx={{
                  width: '24px',
                  height: '24px',
                  color: 'var(--blue-700)',
                  flexShrink: 0,
                }}
                aria-label="Admin lock"
              />
            </div>
          ) : null,
      } as ColumnDef<FranchiseeBundleAddOnRow, unknown>,
      {
        id: 'bundleAddOn',
        accessorKey: 'name',
        header: t('franchiseeBundleAddOns.bundleAddOnColumn'),
        enableSorting: true,
        enableColumnFilter: true,
        sortingFn: (rowA: Row<FranchiseeBundleAddOnRow>, rowB: Row<FranchiseeBundleAddOnRow>) =>
          (rowA.original.name ?? '').localeCompare(rowB.original.name ?? '', undefined, { sensitivity: 'base' }),
        cell: ({ row }: { row: Row<FranchiseeBundleAddOnRow> }) => {
          const addOnRow = row.original;
          return (
            <div className={styles.addOnNameCell}>
              <button
                type="button"
                className={styles.addOnLink}
                onClick={() => {
                  setSelectedAddOn(addOnRow);
                  setEditAddOnModalOpen(true);
                }}
              >
                {addOnRow.name}
              </button>
            </div>
          );
        },
      } as ColumnDef<FranchiseeBundleAddOnRow, unknown>,
    ];

    storeGroupsForColumns.forEach((group, idx) => {
      const storeGroupName = group.storeGroup;
      const displayName = storeGroupName.substring(0, 8);
      list.push({
        id: `sg-${storeGroupName}`,
        accessorFn: (row: FranchiseeBundleAddOnRow) => row.storeGroupPrices?.[storeGroupName]?.priceDisplay ?? '',
        header: () => (
          <div className={styles.storeGroupHeader}>
            <span className={styles.storeGroupHeaderName}>{displayName}</span>
            <span className={styles.storeGroupHeaderCount}>
              {group.selected}/{group.total}
            </span>
          </div>
        ),
        enableSorting: true,
        enableColumnFilter: false,
        sortingFn: (rowA: Row<FranchiseeBundleAddOnRow>, rowB: Row<FranchiseeBundleAddOnRow>, columnId: string) => {
          const a = parsePriceForSort(rowA.getValue(columnId));
          const b = parsePriceForSort(rowB.getValue(columnId));
          return a - b;
        },
        columnClassname: idx === storeGroupsForColumns.length - 1 ? '' : 'dynamicHeader',
        cell: ({ row }: { row: Row<FranchiseeBundleAddOnRow> }) => {
          const priceInfo: FranchiseeBundleAddOnStoreGroupPrice | undefined =
            row.original.storeGroupPrices?.[storeGroupName];
          if (!priceInfo?.priceDisplay) return null;
          const display = priceInfo.priceDisplay.startsWith('$')
            ? priceInfo.priceDisplay
            : `$${priceInfo.priceDisplay}`;
          return priceInfo.isRange ? (
            <div className={priceInfoStyles.rangePriceCell}>
              <div>{display}</div>
              <InfoOutlined
                sx={{ width: '24px', height: '24px', color: '#266DCC', cursor: 'pointer' }}
                aria-label="Price deviations"
                onClick={() => {
                  setPriceDeviationsDrawer({
                    isOpen: true,
                    namedPriceId: row.original.namedPriceId,
                    selectedGroupName: storeGroupName,
                  });
                }}
              />
            </div>
          ) : (
            <span>{display}</span>
          );
        },
      } as ColumnDef<FranchiseeBundleAddOnRow, unknown>);
    });

    return list;
  }, [t, storeGroupsForColumns]);

  const pillLabel = t('franchiseeBundleAddOns.storesLabel', { count: selectedCount });

  if (
    selectedCount === 0 &&
    (storesAndGroups?.stores?.length ?? 0) === 0 &&
    (storesAndGroups?.storeGroups?.length ?? 0) === 0
  ) {
    return null;
  }

  return (
    <div className={styles.page}>
      <div className={styles.headerRow}>
        <Header
          title={t('franchiseeBundleAddOns.title')}
          breadcrumb={{
            to: '/bundles/add-ons',
            label: t('franchiseeBundleAddOns.breadcrumbStoreSelector'),
          }}
          additionalTitleInfo={pillLabel}
          onClickAdditional={() => setModalOpen(true)}
        />
      </div>
      <div className={styles.tableWrapper}>
        <div className={styles.bundleAddOnsTableWrapper}>
          {!(isLoading || isError) && addOnsSortedAlphabetically.length === 0 ? (
            <Box pl={{ sm: 5, md: 7 }} pt={{ sm: 2, md: 4 }} fontSize="2.0rem">
              <p>{t('franchiseeBundleAddOns.noBundleAddOnsAvailable')}</p>
            </Box>
          ) : (
            <BundleUpchargesTable
              title={undefined}
              breadcrumb={undefined}
              data={addOnsSortedAlphabetically}
              columns={columns}
              enableFilters
              enableRowSelection={false}
              noRowsMessage={t('franchiseeBundleAddOns.noBundleAddOnsAvailable')}
              isLoading={isLoading || isError}
              initialSorting={[{ id: 'bundleAddOn', desc: false }]}
            />
          )}
        </div>
      </div>
      {modalOpen && (
        <ModalStores stores={selectedStoreNumbers} isOpen={modalOpen} onClose={() => setModalOpen(false)} />
      )}
      <EditBundleAddOnDrawer
        isOpen={editAddOnModalOpen}
        onClose={() => {
          setEditAddOnModalOpen(false);
          setSelectedAddOn(null);
        }}
        addOn={selectedAddOn}
        storeGroups={storeGroupsForColumns}
        stores={selectedStoreNumbers}
        afterSave={() => refetch()}
      />
      {priceDeviationsDrawer.isOpen && (
        <NamedPricePriceDeviationsDrawer
          isOpen={priceDeviationsDrawer.isOpen}
          onClose={() => setPriceDeviationsDrawer((prev) => ({ ...prev, isOpen: false }))}
          namedPriceId={priceDeviationsDrawer.namedPriceId}
          selectedGroupName={priceDeviationsDrawer.selectedGroupName}
          cameFromEditDrawer={false}
        />
      )}
    </div>
  );
};

export default FZBundleAddOnsView;
